/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { parseDbTimestamp } from '@/lib/dbDate';
import type { Program, VinDetails } from '@/api/models';

/** Program codes match ignoring hyphens. */
const stripHyphens = (s?: string) => (s ?? '').replace(/-/g, '');

/** Whether two program codes refer to the same program (hyphen-insensitive). */
export const programCodesMatch = (a?: string, b?: string) => stripHyphens(a) === stripHyphens(b);

/** Make/model from catalog when code+year maps unambiguously; empty when shared platforms conflict. */
export function lookupVehicleMakeModel(
  programs: Program[],
  programCode?: string,
  modelYear?: string,
): { make?: string; model?: string } {
  if (!programCode || !modelYear) return {};
  const matches = programs.filter(
    (p) =>
      p.modelYear === modelYear &&
      programCodesMatch(p.programCode, programCode) &&
      (p.make || p.model),
  );
  if (!matches.length) return {};
  const first = matches[0];
  const uniform = matches.every(
    (p) => (p.make ?? '') === (first.make ?? '') && (p.model ?? '') === (first.model ?? ''),
  );
  return uniform ? { make: first.make, model: first.model } : {};
}

/** dataEffectiveDate → epoch ms (DB format "yyyy-MM-dd HH:mm:ss-05"). */
const effInstant = (p: Program): number | null => parseDbTimestamp(p.dataEffectiveDate);

function candidates(programs: Program[], d: VinDetails): Program[] {
  const year = d.modelYear ?? '';
  if (!year) return [];
  const byCode = programs.filter(
    (p) => p.modelYear === year && stripHyphens(p.programCode) === stripHyphens(d.programCode),
  );
  if (byCode.length) return byCode;
  // Fallback: match by model designator when AVI program code doesn't align.
  return d.modelDesignator
    ? programs.filter((p) => p.modelYear === year && p.modelDesignator === d.modelDesignator)
    : [];
}

/** Resolve serviceable program for a VIN (latest release on/before build date). */
export function resolveProgramForVin(programs: Program[], d: VinDetails | null): Program | null {
  if (!d) return null;
  const cands = candidates(programs, d);
  if (!cands.length) return null;
  const withEff = cands
    .map((p) => ({ p, eff: effInstant(p) }))
    .filter((x): x is { p: Program; eff: number } => x.eff != null);
  if (!withEff.length) return cands[0];

  const latest = (arr: { p: Program; eff: number }[]) =>
    arr.reduce((a, b) => (b.eff > a.eff || (b.eff === a.eff && b.p.id > a.p.id) ? b : a)).p;

  const buildT = d.buildDate ? Date.parse(`${d.buildDate}T23:59:59Z`) : NaN;
  if (Number.isNaN(buildT)) return latest(withEff);

  const onOrBefore = withEff.filter((x) => x.eff <= buildT);
  if (onOrBefore.length) return latest(onOrBefore);
  // All releases newer than build date: pick earliest.
  return withEff.reduce((a, b) => (b.eff < a.eff || (b.eff === a.eff && b.p.id < a.p.id) ? b : a)).p;
}

/**
 * Program a VIN should open. The backend resolves this against the whole catalog, so trust its
 * answer even when the caller's own catalog can't list that program; catalog matching is only a
 * fallback for responses that predate server-side resolution.
 */
export function programForVin(programs: Program[], d: VinDetails | null): Program | null {
  if (!d?.programId) return resolveProgramForVin(programs, d);
  const listed = programs.find((p) => p.id === d.programId);
  if (listed) return listed;
  return {
    id: d.programId,
    modelYear: d.modelYear ?? '',
    programCode: d.programCode ?? '',
    make: d.make,
    model: d.model,
    modelDesignator: d.modelDesignator,
    engineeringRelease: d.engineeringRelease ?? '',
  };
}
